import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { PilotAssignmentCommands } from '../../services/DispatcherActions';
import { HourCompensationDialog } from './dialogs/HourCompensationDialog';
import Moment from 'moment';
import uuid from 'react-uuid';
import { Dialog } from '../../../../components/layout/dialogs/Dialog';
import { DialogProperties } from '../../../../components/layout/dialogs/DialogProperties';
import TitleContainer from '../../../../components/layout/TitleContainer';
import Spacer from '../../../../components/layout/Spacer';
import { formatStringToList, isNullOrEmpty, isObjectNull } from '../../../../components/helpers/ObjectHelpers';
import { formatMomentToDefault } from '../../../../components/helpers/DateTimeHelpers';
import { getPilotAssignmentCompensationId } from '../helpers/PilotAssignmentHelpers';
import { CardList } from '../../../../components/layout/card/CardList';
import { useHasEditableChanged } from '../hooks/usePilotAssignmentStore';

export const HourCompensationCard = (
    {
        ref,
        onPilotAssignmentChanged,
        assignmentService
    }) => {

    useImperativeHandle(ref, () => ({
        onPilotageChanged() {
            initialize();
        },
    }));

    const [isEditable, setIsEditable] = useState(false);
    const [compensation, setCompensation] = useState(null);
    const [showDialog, setShowDialog] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [cards, setCards] = useState([]);
    const [selectedCompensations, setSelectedCompensations] = useState([]);

    const listRef = useRef(null);

    const hasEditableChanged = useHasEditableChanged();

    useEffect(() => {
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(!hasEditableChanged) return;
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasEditableChanged]);

    return (
        <>
        <Spacer height={30} />
            <TitleContainer
                title="C-tillegg"
                error={selectedCompensations.some(c => c.selected)}
                actions={getTitleActions()}
            />
           {isNullOrEmpty(cards) ? null : <Spacer height={30} /> }
            <CardList
                ref={listRef}
                source={cards}
                disabled={!isEditable}
                canShowNoContentMessage={false}
            />

            {
                showDialog &&
                <HourCompensationDialog
                    onClose={() => setShowDialog(false)}
                    compensation={compensation}
                    callback={onCallback}
                />
            }

            {
                showConfirmationDialog &&
                <Dialog
                    properties={{
                        ...DialogProperties,
                        mode: "confirmation",
                        title: "Slett C-tillegg",
                        text: "&Oslash;nsker du &aring; slette C-tillegg?",
                        onClose: () => {
                            listRef.current.onReset(getPilotAssignmentCompensationId(compensation, "pilotagePilotHourCompensationId"));
                            setShowConfirmationDialog(false);
                        }                                ,
                        onClick: onConfirmCallbackAsync
                    }}

                />
            }

        </>
    )

    function getTitleActions() {
        const actions = [];

        if (isEditable) {
            if (selectedCompensations.some(c => c.selected)) {
                actions.push(
                    {
                        icon: "trash",
                        onClick: onDeleteSelectedClick,
                        iconColor: "white"
                    });
            } else {
                actions.push(
                    {
                        icon: "plus",
                        onClick: () => {
                            setCompensation(null);
                            setShowDialog(true);
                        },
                        iconColor: "white"
                    });
            }
        }

        return actions;
    }

    function initialize() {

        const pilotAssignment = assignmentService.assignment;
        const isEditable = assignmentService.isEditable;
        setIsEditable(isEditable);

        const result = [];

        if (!isObjectNull(pilotAssignment.pilotagePilotCompensation)) {
            if (!isObjectNull(pilotAssignment.pilotagePilotCompensation.pilotagePilotHourCompensations)) {
                pilotAssignment.pilotagePilotCompensation.pilotagePilotHourCompensations.map((compensation) =>
                    result.push({
                        key: uuid(),
                        id: getPilotAssignmentCompensationId(compensation, "pilotagePilotHourCompensationId"),
                        isDirty: !Object.hasOwn(compensation, "pilotagePilotCompensationId"),
                        title: getCompensationTimeDescription(compensation),
                        borderColor: "var(--card-swipe-border)",
                        backgroundColor: "var(--card-swipe-bg)",
                        canDelete: isEditable,
                        onDelete: isEditable ? () => onDeleteClick(compensation) : null,
                        onClick: isEditable ? () => {
                            setCompensation(compensation);
                            setShowDialog(true);
                        } : null,
                        content: <HourCompensationContent compensation={compensation} />
                    }));
            }
        }

        setCards(result);
    }

    function onDeleteClick(compensation) {
        setCompensation(compensation);
        setShowConfirmationDialog(true);
    }

    // implement later when multible delete is fixed
    //function toggleSelectItem(id, selected) {
    //    const result = toggleSelectedItem(selectedCompensations, id, selected);
    //    setSelectedCompensations(result);
    //}

    function onDeleteSelectedClick() {
        setCompensation(null);
        setShowConfirmationDialog(true);
    }

    async function onCallback(obj) {

        switch (obj.command) {

            case PilotAssignmentCommands.AddPilotHourCompensation:
            case PilotAssignmentCommands.UpdatePilotHourCompensation:
                await assignmentService.onPutPilotHourCompensationAsync(obj);
                break;

            case PilotAssignmentCommands.DeletePilotHourCompensation:
                await assignmentService.onDeletePilotHourCompensationAsync(obj);
                break;


            default:
                return;
        }

        onPilotAssignmentChanged();
        initialize();
    }

    async function onConfirmCallbackAsync() {

        if (isObjectNull(compensation)) {
            await onDeletePilotAssignmentHourCompensationsAsync();
        } else {
            await assignmentService.onDeletePilotHourCompensationAsync(compensation);
        }

        setShowConfirmationDialog(false);
        setSelectedCompensations([]);

        onPilotAssignmentChanged();
        initialize();
    }

    async function onDeletePilotAssignmentHourCompensationsAsync() {
        for (const compensation of selectedCompensations) {
            if (!compensation.selected) continue;
            await assignmentService.onDeletePilotHourCompensationAsync(compensation);
        }
    }

    function getCompensationTimeDescription(compensation) {
        const toTime = isNullOrEmpty(compensation.toTime) ?
            Moment(compensation.fromTime).add(compensation.number, "hours") : Moment(compensation.toTime);

        return `${formatMomentToDefault(compensation.fromTime)} &rarr; ${toTime.format('DD.MM HH:mm')}`;
    }
}

const HourCompensationContent = ({ compensation }) => {
    return (
        <>
            <div>
                <span className="pp-compensation-type">{compensation.pilotagePilotHourCompensationName}</span>
            </div>
            <div>{compensation.number} timer</div>
            {!isNullOrEmpty(compensation.description) && (
                <>
                    <Spacer height={5} />
                    <div>
                        <i>{formatStringToList(compensation.description)}</i>
                    </div>
                </>
            )}
        </>
    );
};
